<template>
    <div class="ei-trend">
        <div v-if="chartLoading" class="ei-trend-loading">
            <i class="fas fa-spinner fa-spin"></i> 차트 데이터 로드 중…
        </div>
        <div v-else-if="chartError" class="ei-trend-error">{{ chartError }}</div>
        <template v-else>
            <div v-if="!hasData" class="ei-trend-empty">표시할 월별 계산 데이터가 없습니다.</div>
            <template v-else>
                <div class="ei-trend-charts">
                    <section class="ei-trend-chart-card">
                        <header class="ei-trend-chart-head">
                            <strong>{{ intensityHeadPrimary }}</strong>
                            <span>{{ intensityHeadUnit }}</span>
                        </header>
                        <div ref="intensityChartEl" class="ei-trend-chart"></div>
                    </section>

                    <section class="ei-trend-chart-card">
                        <header class="ei-trend-chart-head">
                            <strong>{{ energyHeadPrimary }}</strong>
                            <span>{{ energyHeadUnit }}</span>
                        </header>
                        <div ref="energyChartEl" class="ei-trend-chart"></div>
                    </section>

                    <section class="ei-trend-chart-card">
                        <header class="ei-trend-chart-head">
                            <strong>{{ baseHeadPrimary }}</strong>
                            <span>{{ baseHeadUnit }}</span>
                        </header>
                        <div ref="baseChartEl" class="ei-trend-chart"></div>
                    </section>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { formatYm, pickLatestCalcPerMonth, enIntMasterCalcListUnion } from "../utils/enIntUi.js";

function toNumberOrNull(v) {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function latestFiniteIndex(values) {
    for (let i = values.length - 1; i >= 0; i -= 1) {
        if (Number.isFinite(values[i])) return i;
    }
    return -1;
}

export default {
    name: "EnIntDataTrendChart",
    props: {
        enIntIdx: { type: Number, required: true },
        masterApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        sharedCalcListApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        dateType: { type: String, required: true },
        dateValue: { type: Number, required: true },
        refreshToken: { type: Number, default: 0 },
    },
    data() {
        return {
            points: [],
            compareValues: {
                mom: { energy: [], base: [], intensity: [] },
                yoy: { energy: [], base: [], intensity: [] },
            },
            compareRanges: {
                base: null,
                spanMonths: 0,
            },
            charts: {
                energy: null,
                base: null,
                intensity: null,
            },
        };
    },
    computed: {
        chartLoading() {
            return Boolean(this.sharedCalcListApiState?.loading);
        },
        chartError() {
            if (this.chartLoading) return "";
            const s = this.sharedCalcListApiState;
            if (s?.error) return s.error;
            return "";
        },
        queryKey() {
            const s = this.sharedCalcListApiState;
            const rowSig = s?.data?.rows?.length
                ? `${s.data.rows.length}:${s.data.rows[0]?.regYm ?? ""}:${s.data.rows[s.data.rows.length - 1]?.regYm ?? ""}`
                : "";
            return [
                this.enIntIdx,
                this.dateType,
                this.dateValue,
                this.refreshToken,
                s?.loading,
                s?.error || "",
                rowSig,
            ].join(":");
        },
        master() {
            return this.masterApiState?.data || {};
        },
        /** BASE / REF / RESULT 관제점 — 차트 헤더에 ptName·(단위) 반영 */
        sources() {
            return Array.isArray(this.master?.sources) ? this.master.sources : [];
        },
        hasData() {
            return this.points.length > 0;
        },
        months() {
            return this.points.map((p) => formatYm(p.regYm));
        },
        energyValues() {
            return this.points.map((p) => p.energy);
        },
        baseValues() {
            return this.points.map((p) => p.base);
        },
        intensityValues() {
            return this.points.map((p) => p.intensity);
        },
        momEnergyValues() {
            return this.compareValues.mom.energy || [];
        },
        momBaseValues() {
            return this.compareValues.mom.base || [];
        },
        momIntensityValues() {
            return this.compareValues.mom.intensity || [];
        },
        yoyEnergyValues() {
            return this.compareValues.yoy.energy || [];
        },
        yoyBaseValues() {
            return this.compareValues.yoy.base || [];
        },
        yoyIntensityValues() {
            return this.compareValues.yoy.intensity || [];
        },
        baseRangeLabel() {
            const r = this.compareRanges.base;
            if (!r) return "—";
            return `${formatYm(r.fromYm)} ~ ${formatYm(r.toYm)}`;
        },
        energyUnitLabel() {
            const u = (this.master.energyUnit || "").trim();
            return u || "kWh";
        },
        baseUnitLabel() {
            return (this.master.baseUnit || "").trim() || "단위";
        },
        intensityUnitLabel() {
            return (this.master.intensityUnit || "").trim() || "단위";
        },
        energyHeadPrimary() {
            const refs = (this.sources || []).filter((s) => String(s.sourceType || "").toUpperCase() === "REF");
            const names = refs.map((s) => (s.ptName || s.ptAddr || "").trim()).filter(Boolean);
            return names.length ? names.join(" · ") : "대상 관제점 값";
        },
        energyHeadUnit() {
            return `(${this.energyUnitLabel})`;
        },
        baseHeadPrimary() {
            const b = (this.sources || []).find((s) => String(s.sourceType || "").toUpperCase() === "BASE");
            if (b && (b.ptName || b.ptAddr)) return String(b.ptName || b.ptAddr).trim();
            return "기준값";
        },
        baseHeadUnit() {
            return `(${this.baseUnitLabel})`;
        },
        intensityHeadPrimary() {
            const r = (this.sources || []).find((s) => String(s.sourceType || "").toUpperCase() === "RESULT");
            if (r && (r.ptName || r.ptAddr)) return String(r.ptName || r.ptAddr).trim();
            return "원단위";
        },
        intensityHeadUnit() {
            return `(${this.intensityUnitLabel})`;
        },
    },
    watch: {
        queryKey: {
            immediate: true,
            handler() {
                this.applyFromSharedCalcList();
            },
        },
        master: {
            deep: true,
            handler() {
                this.scheduleRender();
            },
        },
        sources: {
            deep: true,
            handler() {
                this.scheduleRender();
            },
        },
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
        this.disposeAllCharts();
    },
    methods: {
        fmt(v) {
            if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 });
        },
        toPointFromRow(r) {
            return {
                regYm: Number(r?.regYm),
                energy: toNumberOrNull(r?.refKwhVal),
                base: toNumberOrNull(r?.baseVal),
                intensity: toNumberOrNull(r?.calcVal),
            };
        },
        /**
         * 월별 최신 calc 행을 regYm 오름차순으로 정렬해 포인트·비교 시리즈를 맞춤.
         * 전월·전년 값은 행의 mom / yoy(서버)만 사용 — 다른 월 행을 찾아 맞추지 않음.
         */
        buildPointsAndCompareFromRows(calcRows) {
            const latestMap = pickLatestCalcPerMonth(calcRows);
            const sortedRows = Array.from(latestMap.values()).sort((a, b) => Number(a.regYm) - Number(b.regYm));
            const points = [];
            const momE = [];
            const yoyE = [];
            const momB = [];
            const yoyB = [];
            const momI = [];
            const yoyI = [];
            for (const r of sortedRows) {
                const p = this.toPointFromRow(r);
                if (!Number.isFinite(p.regYm)) continue;
                points.push(p);
                const mom = r?.mom;
                const yoy = r?.yoy;
                momI.push(toNumberOrNull(mom?.prevCalcVal ?? mom?.prevVal));
                yoyI.push(toNumberOrNull(yoy?.yoyCalcVal ?? yoy?.yoyVal));
                momE.push(toNumberOrNull(mom?.prevRefKwhVal ?? mom?.prevRefKwh ?? mom?.refKwhVal));
                yoyE.push(toNumberOrNull(yoy?.yoyRefKwhVal ?? yoy?.yoyRefKwh ?? yoy?.refKwhVal));
                momB.push(toNumberOrNull(mom?.prevBaseVal ?? mom?.prevBase));
                yoyB.push(toNumberOrNull(yoy?.yoyBaseVal ?? yoy?.yoyBase));
            }
            return {
                points,
                compareValues: {
                    mom: { energy: momE, base: momB, intensity: momI },
                    yoy: { energy: yoyE, base: yoyB, intensity: yoyI },
                },
            };
        },
        emptyCompareState() {
            return {
                mom: { energy: [], base: [], intensity: [] },
                yoy: { energy: [], base: [], intensity: [] },
            };
        },
        /** 상위 EnIntMasterMain의 sharedCalcListApiState(union 구간)로부터 추이 시리즈 구성 */
        applyFromSharedCalcList() {
            /* 로딩 중에는 차트 DOM이 v-if로 제거되므로, 기존 인스턴스를 남기면 새 DOM에 붙지 않음 */
            this.disposeAllCharts();
            try {
                const u = enIntMasterCalcListUnion(this.dateType, this.dateValue);
                if (!this.enIntIdx || !u) {
                    this.points = [];
                    this.compareValues = this.emptyCompareState();
                    this.compareRanges = { base: null, spanMonths: 0 };
                    this.scheduleRender();
                    return;
                }
                const s = this.sharedCalcListApiState;
                if (!s || s.loading) {
                    this.points = [];
                    this.compareValues = this.emptyCompareState();
                    this.compareRanges = { base: null, spanMonths: 0 };
                    this.scheduleRender();
                    return;
                }
                if (s.error && !s.data) {
                    this.points = [];
                    this.compareValues = this.emptyCompareState();
                    this.compareRanges = { base: null, spanMonths: 0 };
                    this.scheduleRender();
                    return;
                }
                const baseRange = u.baseRange;
                const spanMonths = u.spanMonths;
                const allRows = Array.isArray(s.data?.rows) ? s.data.rows : [];
                const baseRows = allRows.filter((r) => {
                    const ym = Number(r.regYm);
                    return ym >= baseRange.fromYm && ym <= baseRange.toYm;
                });
                const built = this.buildPointsAndCompareFromRows(baseRows);
                this.points = built.points;
                this.compareValues = built.compareValues;
                this.compareRanges = {
                    base: baseRange,
                    spanMonths,
                };
            } catch {
                this.points = [];
                this.compareValues = this.emptyCompareState();
                this.compareRanges = { base: null, spanMonths: 0 };
            } finally {
                this.scheduleRender();
            }
        },
        refresh() {
            this.applyFromSharedCalcList();
        },
        scheduleRender() {
            this.$nextTick(() => this.renderCharts());
        },
        onResize() {
            Object.values(this.charts).forEach((chart) => chart?.resize());
        },
        resize() {
            this.onResize();
        },
        disposeAllCharts() {
            Object.keys(this.charts).forEach((k) => {
                this.charts[k]?.dispose();
                this.charts[k] = null;
            });
        },
        ensureChart(key, el) {
            if (!el) return null;
            const prev = this.charts[key];
            if (prev) {
                let prevDom = null;
                try {
                    prevDom = typeof prev.getDom === "function" ? prev.getDom() : null;
                } catch {
                    prevDom = null;
                }
                if (prevDom !== el) {
                    try {
                        prev.dispose();
                    } catch {
                        /* ignore */
                    }
                    this.charts[key] = null;
                }
            }
            if (!this.charts[key]) {
                this.charts[key] = echarts.init(el, null, { renderer: "canvas" });
            }
            return this.charts[key];
        },
        hasFiniteValue(values) {
            return (values || []).some((v) => Number.isFinite(v));
        },
        renderMetricChart({ key, el, title, unit, values, momValues, yoyValues, color, areaColor }) {
            const chart = this.ensureChart(key, el);
            if (!chart) return;

            const categories = this.months;
            const hasBase = this.hasFiniteValue(values);
            const hasMom = this.hasFiniteValue(momValues);
            const hasYoy = this.hasFiniteValue(yoyValues);
            const hasValue = hasBase || hasMom || hasYoy;
            const latestIdx = latestFiniteIndex(values);
            const latestVal = latestIdx >= 0 ? values[latestIdx] : null;

            if (!hasValue) {
                chart.setOption(
                    {
                        animation: false,
                        xAxis: { show: false },
                        yAxis: { show: false },
                        series: [],
                        graphic: {
                            type: "text",
                            left: "center",
                            top: "middle",
                            style: {
                                text: "데이터 없음",
                                fill: "#94a3b8",
                                fontSize: 12,
                                fontWeight: 600,
                            },
                        },
                    },
                    true,
                );
                chart.resize();
                return;
            }

            const series = [];
            if (hasBase) {
                series.push({
                    name: "기준",
                    type: "line",
                    connectNulls: true,
                    smooth: 0.24,
                    symbol: "circle",
                    symbolSize: 5,
                    lineStyle: { width: 2.2, color },
                    itemStyle: { color },
                    areaStyle: areaColor ? { color: areaColor } : undefined,
                    markPoint:
                        latestIdx >= 0
                            ? {
                                  symbol: "circle",
                                  symbolSize: 9,
                                  itemStyle: { color },
                                  label: { show: false },
                                  data: [{ coord: [categories[latestIdx], latestVal] }],
                              }
                            : undefined,
                    data: values,
                    z: 5,
                });
            }
            if (hasMom) {
                series.push({
                    name: "전월",
                    type: "line",
                    connectNulls: false,
                    smooth: 0.12,
                    symbol: "none",
                    lineStyle: { width: 1.5, color: "#475569", type: "dashed" },
                    itemStyle: { color: "#475569" },
                    data: momValues,
                    z: 3,
                });
            }
            if (hasYoy) {
                series.push({
                    name: "전년",
                    type: "line",
                    connectNulls: false,
                    smooth: 0.12,
                    symbol: "none",
                    lineStyle: { width: 1.4, color: "#94a3b8", type: "dotted" },
                    itemStyle: { color: "#94a3b8" },
                    data: yoyValues,
                    z: 2,
                });
            }

            chart.setOption(
                {
                    animationDuration: 240,
                    color: [color],
                    grid: { top: hasMom || hasYoy ? 24 : 10, left: 48, right: 14, bottom: 26 },
                    legend: {
                        show: hasMom || hasYoy,
                        top: 0,
                        right: 0,
                        itemWidth: 10,
                        itemHeight: 2,
                        icon: "rect",
                        textStyle: { color: "#94a3b8", fontSize: 9, fontWeight: 700 },
                        selectedMode: false,
                    },
                    tooltip: {
                        trigger: "axis",
                        axisPointer: { type: "line" },
                        backgroundColor: "rgba(15, 23, 42, 0.92)",
                        borderWidth: 0,
                        textStyle: { color: "#f8fafc", fontSize: 11 },
                        formatter: (items) => {
                            const idx = items?.[0]?.dataIndex ?? 0;
                            const ym = categories[idx] || "-";
                            const baseVal = Number.isFinite(values[idx]) ? this.fmt(values[idx]) : "—";
                            const momVal = Number.isFinite(momValues[idx]) ? this.fmt(momValues[idx]) : "—";
                            const yoyVal = Number.isFinite(yoyValues[idx]) ? this.fmt(yoyValues[idx]) : "—";
                            const rows = [
                                `<div>기준: ${baseVal} ${unit}</div>`,
                                `<div>전월: ${momVal} ${unit}</div>`,
                                `<div>전년: ${yoyVal} ${unit}</div>`,
                            ].join("");
                            return `<div style="margin-bottom:4px;font-weight:700;">${ym} · ${title}</div>${rows}`;
                        },
                    },
                    xAxis: {
                        type: "category",
                        data: categories,
                        axisLabel: { color: "#64748b", fontSize: 10 },
                        axisLine: { lineStyle: { color: "#cbd5e1" } },
                        axisTick: { show: false },
                    },
                    yAxis: {
                        type: "value",
                        scale: true,
                        axisLabel: {
                            color: "#64748b",
                            fontSize: 10,
                            formatter: (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }),
                        },
                        axisLine: { lineStyle: { color: "#cbd5e1" } },
                        axisTick: { show: false },
                        splitLine: { lineStyle: { color: "rgba(226, 232, 240, 0.95)" } },
                    },
                    series,
                },
                true,
            );

            chart.resize();
        },
        renderCharts() {
            if (!this.hasData) {
                this.disposeAllCharts();
                return;
            }

            if (!this._resizeBound) {
                window.addEventListener("resize", this.onResize);
                this._resizeBound = true;
            }

            this.renderMetricChart({
                key: "intensity",
                el: this.$refs.intensityChartEl,
                title: "원단위",
                unit: this.intensityUnitLabel,
                values: this.intensityValues,
                momValues: this.momIntensityValues,
                yoyValues: this.yoyIntensityValues,
                color: "#0e7490",
                areaColor: "rgba(14, 116, 144, 0.08)",
            });

            this.renderMetricChart({
                key: "energy",
                el: this.$refs.energyChartEl,
                title: "대상 관제점 값",
                unit: this.energyUnitLabel,
                values: this.energyValues,
                momValues: this.momEnergyValues,
                yoyValues: this.yoyEnergyValues,
                color: "#0ea5e9",
                areaColor: "rgba(14, 165, 233, 0.08)",
            });

            this.renderMetricChart({
                key: "base",
                el: this.$refs.baseChartEl,
                title: "기준값",
                unit: this.baseUnitLabel,
                values: this.baseValues,
                momValues: this.momBaseValues,
                yoyValues: this.yoyBaseValues,
                color: "#f59e0b",
                areaColor: "rgba(245, 158, 11, 0.08)",
            });
        },
    },
};
</script>

<style scoped>
.ei-trend {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    overflow: auto;
}

.ei-trend-loading,
.ei-trend-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    border: 1px dashed rgba(203, 213, 225, 0.95);
    border-radius: 8px;
    background: rgba(248, 250, 252, 0.75);
    color: #94a3b8;
    font-size: 12px;
    font-weight: 600;
}

.ei-trend-error {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(239, 68, 68, 0.2);
    background: rgba(239, 68, 68, 0.06);
    color: #b91c1c;
    font-size: 12px;
    font-weight: 600;
}

.ei-trend-compare-caption {
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    background: rgba(248, 250, 252, 0.78);
    color: #64748b;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.35;
    font-variant-numeric: tabular-nums;
}

.ei-trend-charts {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
}

.ei-trend-chart-card {
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 8px;
    background: #fff;
    padding: 8px 10px;
}

.ei-trend-chart-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
    margin-bottom: 6px;
}

.ei-trend-chart-head strong {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    line-height: 1.35;
    min-width: 0;
}

.ei-trend-chart-head span {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
}

.ei-trend-chart {
    width: 100%;
    height: 170px;
}

@media (max-width: 640px) {
    .ei-trend-chart {
        height: 190px;
    }
}
</style>
